/**
 * AlphaX Tuning Simulator | Copyright (C) 2026 AlphaX
 * Licensed under GPL-3.0 (https://www.gnu.org/licenses/gpl-3.0.txt)
 * Subscriptions do not negate this license; derivative works must be open source.
 */
/**
 * AlphaX Tuning Simulator - Engine Data & Profiles
 */

let sampleEngine = {
    "schema_type": "engine_profile",
    "global_specs": {
        "engine_id": "d16a6",
        "display_name": "Honda D16A6 (SOHC)",
        "engine_layout": "Inline",
        "cylinders": 4,
        "firing_order": "1342",
        "displacement_cc": 1590,
        "compression_ratio": 9.1,
        "required_octane": 87,
        "true_deadtime_ms": 0.70,
        "default_vehicle_weight_lbs": 2250,
        "trigger_base_offset": 0,
        "ideal_idle_rpm": 800,
        "bore_mm": 75.0,
        "stroke_mm": 90.0,
        "ignition_system_factory": "distributor",
        "min_coil_dwell_ms": 2.0,
        "spark_plug_gap_in": 0.040
    },
    "fuel_system_stock": {
        "stock_injector_cc": 240,
        "stock_fuel_pump_lph": 79,
        "base_fuel_pressure_psi": 38.0,
        "true_deadtime_ms": 0.7
    },
    "mechanical_limits": {
        "redline_rpm": 6600,
        "max_safe_rpm": 8000,
        "max_cylinder_pressure_tq": 250,
        "stock_flywheel_inertia_kgm2": 0.2,
        "mechanical_friction_coeff": 0.008,
        "idle_throttle_area_scale": 1,
        "oem_clutch_torque_lbft": 155,
        "oem_clutch_heat_kj": 76
    },
    "vvt_parameters": {
        "vvt_mode": "None",
        "vvt_max_angle_intake": 0,
        "vvt_max_angle_exhaust": 0
    },
    "performance_maps": {
        "Base": {
            "1000": {"base_torque": 75.0,    "ideal_ve": 68.0,   "ideal_afr": 14.7,  "ideal_timing_mbt": 12.0},
            "1500": {"base_torque": 82.0,    "ideal_ve": 72.0,   "ideal_afr": 14.7,  "ideal_timing_mbt": 16.0},
            "2000": {"base_torque": 88.0,    "ideal_ve": 78.0,   "ideal_afr": 14.7,  "ideal_timing_mbt": 21.0},
            "2500": {"base_torque": 92.0,    "ideal_ve": 82.0,   "ideal_afr": 14.7,  "ideal_timing_mbt": 24.0},
            "3000": {"base_torque": 97.0,    "ideal_ve": 84.0,   "ideal_afr": 14.7,  "ideal_timing_mbt": 26.0},
            "3500": {"base_torque": 98.0,    "ideal_ve": 86.0,   "ideal_afr": 14.5,  "ideal_timing_mbt": 27.0},
            "4000": {"base_torque": 100.188, "ideal_ve": 88.0,   "ideal_afr": 14.2,  "ideal_timing_mbt": 28.0},
            "4500": {"base_torque": 102.0,   "ideal_ve": 90.0,   "ideal_afr": 13.8,  "ideal_timing_mbt": 29.0},
            "5000": {"base_torque": 102.0,   "ideal_ve": 89.0,   "ideal_afr": 13.35, "ideal_timing_mbt": 30.0},
            "5500": {"base_torque": 101.813, "ideal_ve": 87.0,   "ideal_afr": 13.05, "ideal_timing_mbt": 31.0},
            "6000": {"base_torque": 91.0,    "ideal_ve": 84.0,   "ideal_afr": 12.95, "ideal_timing_mbt": 32.0},
            "6500": {"base_torque": 81.0,    "ideal_ve": 80.0,   "ideal_afr": 12.85, "ideal_timing_mbt": 32.0},
            "7000": {"base_torque": 72.0,    "ideal_ve": 75.0,   "ideal_afr": 12.85, "ideal_timing_mbt": 33.0},
            "7500": {"base_torque": 54.709,  "ideal_ve": 65.0,   "ideal_afr": 12.836,"ideal_timing_mbt": 33.108},
            "8000": {"base_torque": 37.501,  "ideal_ve": 53.379, "ideal_afr": 12.647,"ideal_timing_mbt": 33.464},
            "8500": {"base_torque": 18.638,  "ideal_ve": 25.0,   "ideal_afr": 12.54, "ideal_timing_mbt": 33.464},
            "9000": {"base_torque": 6.063,   "ideal_ve": 10.0,   "ideal_afr": 12.379,"ideal_timing_mbt": 33.583}
        }
    },
    "default_transmission": {
        "trans_id": "Honda L3 5-speed",
        "final_drive": 4.250,
        "gears": [3.250, 1.894, 1.259, 0.937, 0.771, 0, 0, 0, 0, 0]
    }
};

// ── Standalone fallbacks for TuningSim.html dev use ──────────────────────────
// In Shopify, Liquid injects window.hostedMods / window.hostedEngines /
// window.hostedTransmissions (filtered by customer tags) before this script loads.

const _defaultHostedMods = [
    // ── Camshafts ────────────────────────────────────────────────────────────
    { type: 'Camshaft', name: 'Stage 1 Cam',                      url: 'Stage 1 Cam.xsim-mod' },
    { type: 'Camshaft', name: 'Stage 2 Cam',                      url: 'Stage 2 Cam.xsim-mod' },
    { type: 'Camshaft', name: 'Stage 3 Cam',                      url: 'Stage 3 Cam.xsim-mod' },
    { type: 'Camshaft', name: 'Stage 4 Cam',                      url: 'Stage 4 Cam.xsim-mod' },
    // ── Intakes ──────────────────────────────────────────────────────────────
    { type: 'Intake', name: 'AlphaX Cold Air Intake',             url: 'AlphaX Cold Air Intake.xsim-mod' },
    { type: 'Intake', name: 'Good Value Short Ram Intake',        url: 'Good Value Short Ram Intake.xsim-mod' },
    { type: 'Intake', name: 'eBay Short Ram Intake',              url: 'eBay Short Ram Intake.xsim-mod' },
    // ── Intake Manifolds ─────────────────────────────────────────────────────
    { type: 'Intake Manifold', name: 'GLARE Engineering Velocity Plenum',    url: 'GLARE Engineering Velocity Plenum.xsim-mod' },
    { type: 'Intake Manifold', name: 'Long Runner Torque Manifold',          url: 'Long Runner Torque Manifold.xsim-mod' },
    { type: 'Intake Manifold', name: 'Ported Stock Manifold',                url: 'Ported Stock Manifold.xsim-mod' },
    { type: 'Intake Manifold', name: 'Short Runner Performance Manifold',    url: 'Short Runner Performance Manifold.xsim-mod' },
    // ── Exhaust Manifold ─────────────────────────────────────────────────────
    { type: 'Exhaust Manifold', name: 'Equal Length Header',      url: 'Equal Length Header.xsim-mod' },
    // ── Exhaust ──────────────────────────────────────────────────────────────
    { type: 'Exhaust', name: 'Dorla HeaderBack',                  url: 'Dorla HeaderBack.xsim-mod' },
    { type: 'Exhaust', name: 'eBay CatBack',                      url: 'eBay CatBack.xsim-mod' },
    { type: 'Exhaust', name: 'eBay Muffler',                      url: 'eBay Muffler.xsim-mod' },
    // ── Clutch ───────────────────────────────────────────────────────────────
    { type: 'Clutch', name: 'Stage 1 Performance Clutch',         url: 'Stage 1 Performance Clutch.xsim-mod' },
    { type: 'Clutch', name: 'Stage 2 Performance Clutch',         url: 'Stage 2 Performance Clutch.xsim-mod' },
    { type: 'Clutch', name: 'Stage 3 Racing Clutch',              url: 'Stage 3 Racing Clutch.xsim-mod' },
    { type: 'Clutch', name: 'Carbon Racing Clutch',               url: 'Carbon Racing Clutch.xsim-mod' },
    { type: 'Clutch', name: 'Triple Disc Carbon Clutch',          url: 'Triple Disc Carbon Clutch.xsim-mod' },
    // ── Turbochargers ────────────────────────────────────────────────────────
    { type: 'Turbocharger', name: 'Mitsy TD05HR-16G',             url: 'mitsy_td05hr_16g.xsim-mod' },
    { type: 'Turbocharger', name: 'Precise 6062',                 url: 'precise_6062.xsim-mod' },
    // ── Intercoolers ─────────────────────────────────────────────────────────
    { type: 'Cooling', name: 'Budget Air-to-Air Intercooler',     url: 'Intercoolers/budget_ata_ic.xsim-mod' },
    { type: 'Cooling', name: 'Precision High-Flow Intercooler',   url: 'Intercoolers/precision_hf_ic.xsim-mod' },
    { type: 'Cooling', name: 'Extreme Race Intercooler (Ice Box)',url: 'Intercoolers/extreme_race_ic.xsim-mod' },
    { type: 'Cooling', name: 'Entry-Level Intercooler Plate (Roots)', url: 'Intercoolers/entry_plate_ic.xsim-mod' },
    { type: 'Cooling', name: 'Performance Dual-Pass Intercooler (Roots)', url: 'Intercoolers/dual_pass_ic.xsim-mod' },
    { type: 'Cooling', name: 'Billet High-Efficiency Intercooler (Roots)', url: 'Intercoolers/billet_he_ic.xsim-mod' },
    // ── Fuel Pumps ───────────────────────────────────────────────────────────
    { type: 'FuelPump', name: 'Injector Xpress 450 LPH',          url: 'ix_pump_450lph.xsim-mod' },
    { type: 'FuelPump', name: 'Injector Xpress 525 LPH',          url: 'ix_pump_525lph.xsim-mod' },
    { type: 'FuelPump', name: 'Injector Xpress 800 LPH',          url: 'ix_pump_800lph.xsim-mod' },
    { type: 'FuelPump', name: 'Injector Xpress Twin 800 LPH',     url: 'ix_pump_twin_800lph.xsim-mod' },
    // ── Injectors ────────────────────────────────────────────────────────────
    { type: 'Injector', name: 'Injector Xpress 440cc',            url: 'ix_440cc.xsim-mod' },
    { type: 'Injector', name: 'Injector Xpress 550cc',            url: 'ix_550cc.xsim-mod' },
    { type: 'Injector', name: 'Injector Xpress 650cc',            url: 'ix_650cc.xsim-mod' },
    { type: 'Injector', name: 'Injector Xpress 750cc',            url: 'ix_750cc.xsim-mod' },
    { type: 'Injector', name: 'Injector Xpress 850cc',            url: 'ix_850cc.xsim-mod' },
    { type: 'Injector', name: 'Injector Xpress 1000cc',           url: 'ix_1000cc.xsim-mod' },
    { type: 'Injector', name: 'Injector Xpress 1200cc',           url: 'ix_1200cc.xsim-mod' },
    { type: 'Injector', name: 'Injector Xpress 1600cc',           url: 'ix_1600cc.xsim-mod' },
    { type: 'Injector', name: 'Injector Xpress 2600cc',           url: 'ix_2600cc.xsim-mod' },
];

const hostedMods = Array.isArray(window.hostedMods) ? window.hostedMods : _defaultHostedMods;
const sampleModsDb = {};
const lastLoadedMods = {};

// Snapshot of the hardcoded D16A6 default — used to restore when user picks "d16a6_default"
// from the dropdown, even after sampleEngine has been overwritten by another engine load.
const _defaultD16A6Engine = sampleEngine;

// Local engine URLs (Shopify will use CDN URLs injected via window.hostedEngines)
// D16A6 is always the free default — it is NOT in this list.
// Transmission data is read directly from each engine's .xsim-engine file on demand.
const _defaultHostedEngines = [
    { id: 'd16z6',  name: 'Honda D16Z6 (SOHC VTEC)',     url: 'd16z6.xsim-engine' },
    { id: '4g63',   name: 'Mitsubishi 4G63T (Inline-4)', url: '4G63.xsim-engine' },
    { id: 'k20a2',  name: 'Honda K20A2 (Inline-4)',      url: 'k20a2.xsim-engine' },
    { id: '2zzge',  name: 'Toyota 2ZZ-GE (Inline-4)',    url: '2zzge.xsim-engine' },
    { id: '2jzge',  name: 'Toyota 2JZ-GE (Inline-6)',    url: '2jzge.xsim-engine' },
];

const hostedEngines = Array.isArray(window.hostedEngines) ? window.hostedEngines : _defaultHostedEngines;

let lastLoadedEngine = null;

async function fetchEngine(url) {
    console.log("Attempting to fetch engine from:", url);
    try {
        const response = await fetch(url);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const loadedEngine = await response.json();
        console.log("Successfully fetched engine:", loadedEngine.global_specs?.display_name);
        lastLoadedEngine = loadedEngine;
        loadEngine(loadedEngine);
    } catch (err) {
        console.error("Could not fetch engine file:", err);
        const selector = document.getElementById('engine-selector');
        if (window.location.protocol === 'file:') {
            alert("Browser security blocks loading engines from the list when running locally (file://).\n\nPlease use the '+ Load Custom' option and select the file from your 'engine_models' folder manually.");
            if (selector) selector.value = lastLoadedEngine ? 'loaded_custom' : '';
        } else {
            alert("Failed to load engine: " + err.message);
            if (selector) selector.value = lastLoadedEngine ? 'loaded_custom' : '';
        }
    }
}

function loadEngine(newEngineData) {
    const wasRunning = isEngineRunning;
    if (wasRunning) document.getElementById('btn-power').click();

    sampleEngine = newEngineData;
    physics = new AlphaXPhysics(sampleEngine, ecu);

    // Reset coil melt state for the new engine
    ecu.disabledCylinders = new Set();
    ecu.meltedCoils       = new Set();
    if (typeof coilMeltAccumulator !== 'undefined') coilMeltAccumulator = 0;

    // Pre-populate plug gap from engine spec
    const stockGap = sampleEngine.global_specs?.spark_plug_gap_in ?? 0.040;
    ecu.sparkPlugGapIn = stockGap;
    const elGap = document.getElementById('inp-spark-gap');
    if (elGap) elGap.value = stockGap.toFixed(3);
    
    // Firing order — supports array, space-separated string (for >9 cylinders), or legacy single-digit string
    const fo = sampleEngine.global_specs?.firing_order;
    if (Array.isArray(fo)) {
        firingOrderArr = fo.map(Number);
    } else {
        const foStr = String(fo || "1 3 4 2");
        firingOrderArr = foStr.includes(' ')
            ? foStr.trim().split(/\s+/).map(Number)
            : foStr.split('').map(Number);
    }

    // Trigger base offset (physical trigger tooth position relative to TDC)
    window.engineTriggerBaseOffset = sampleEngine.global_specs?.trigger_base_offset ?? 0;

    // Injector deadtime curve — default flat 1ms; tuner must calibrate per injector
    ecu.injDtCurve = [1.0, 1.0, 1.0, 1.0, 1.0];
    
    // Reset gear compensation curves
    ecu.boostGearAxis = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10];
    ecu.boostDutyGearComp = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    ecu.boostTargetGearComp = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    
    // Auto-Fill Drag Config Weight based on new Engine data if it exists
    if (sampleEngine.global_specs.default_vehicle_weight_lbs) {
        const dragWeightInput = document.getElementById('drag-weight');
        if (dragWeightInput) dragWeightInput.value = sampleEngine.global_specs.default_vehicle_weight_lbs;
    }

    // Apply defaults from Engine limits
    if (sampleEngine.fuel_system_stock) {
        const fss = sampleEngine.fuel_system_stock;
        if (fss.stock_injector_cc) {
            const elInj = document.getElementById('inp-inj-size');
            if (elInj) elInj.value = fss.stock_injector_cc;
        }
        if (ecu) {
            ecu.stockFuelPumpLph   = fss.stock_fuel_pump_lph    ?? 130;
            ecu.baseFuelPressurePsi = fss.base_fuel_pressure_psi ?? 43.5;
            ecu.effectiveFuelPressurePsi = ecu.baseFuelPressurePsi;
            const elFP = document.getElementById('inp-base-fuel-pressure');
            if (elFP) elFP.value = ecu.baseFuelPressurePsi;
        }
    }

    const elRevLim = document.getElementById('inp-rev-limit');
    if (elRevLim) elRevLim.value = sampleEngine.mechanical_limits.redline_rpm;
    const elOctane = document.getElementById('inp-fuel-octane');
    if (elOctane) elOctane.value = sampleEngine.global_specs.required_octane || 91;

    // Default ignition system mode from engine spec
    const elIgnMode = document.getElementById('inp-ign-mode');
    if (elIgnMode && sampleEngine.global_specs?.ignition_system_factory) {
        elIgnMode.value = sampleEngine.global_specs.ignition_system_factory;
    }
    
    if (typeof updateIdleSettings === 'function') updateIdleSettings();
    if (typeof updateVVTMenuVisibility === 'function') updateVVTMenuVisibility();
    
    // Re-apply any selected upgrades to the newly loaded engine
    if (typeof applyVehicleMods === 'function') applyVehicleMods();

    if (audio) audio.setEngineProfile(sampleEngine.global_specs.cylinders, sampleEngine.global_specs.engine_layout);
    if (typeof setupEngineVisuals === 'function') setupEngineVisuals();
    if (typeof updateTriggerVisual === 'function') updateTriggerVisual();

    // --- DEFAULT TRANSMISSION LOADING ---
    if (sampleEngine.default_transmission) {
        const dt = sampleEngine.default_transmission;
        currentTransmission.name = dt.trans_id || "Engine Default";
        currentTransmission.final_drive = dt.final_drive || 4.0;
        for (let i = 0; i < 10; i++) {
            currentTransmission.gears[i] = (dt.gears && dt.gears[i] !== undefined) ? dt.gears[i] : 0;
        }
        
        // Sync the UI
        if (typeof renderTransmissionUI === 'function') renderTransmissionUI();
        
        // Update the selector to show it's using the engine's default
        const transSelector = document.getElementById('trans-selector');
        if (transSelector) {
            let engineOpt = [...transSelector.options].find(o => o.value === 'engine_default');
            if (!engineOpt) {
                engineOpt = document.createElement('option');
                engineOpt.value = 'engine_default';
                transSelector.insertBefore(engineOpt, transSelector.firstChild);
            }
            engineOpt.text = `Default: ${currentTransmission.name}`;
            transSelector.value = 'engine_default';
            
            const customSettings = document.getElementById('trans-custom-settings');
            if (customSettings) customSettings.classList.add('hidden');
        }
    } else {
        // No default_transmission in this engine — mark the engine_default option as unavailable
        // and reset the selector away from engine_default if it was selected.
        const transSelector = document.getElementById('trans-selector');
        if (transSelector) {
            const engineOpt = [...transSelector.options].find(o => o.value === 'engine_default');
            if (engineOpt) engineOpt.text = 'Default: (none)';
            if (transSelector.value === 'engine_default') {
                const firstOther = [...transSelector.options].find(o => o.value !== 'engine_default');
                transSelector.value = firstOther ? firstOther.value : '';
            }
        }
    }

    if (audio) audio.triggerHoodClose();
}

// --- ENGINE SELECTOR POPULATION & FILE UPLOAD ---

const engineSelector = document.getElementById('engine-selector');
if (engineSelector) {
    // D16A6 is always the hardcoded default — always show it first.
    // Filter it from hostedEngines to avoid a duplicate entry.
    const purchasedEngines = hostedEngines.filter(e => e.id !== 'd16a6');

    let selectorHtml = `<option value="d16a6_default">${sampleEngine.global_specs.display_name}</option>`;
    selectorHtml += purchasedEngines.map(e => `<option value="${e.url}">${e.name}</option>`).join('');
    if (typeof window.shopEnginesUrl === 'string' && window.shopEnginesUrl) {
        selectorHtml += `<option value="shop">Go to Shop!</option>`;
    }
    if (window.location.protocol === 'file:') {
        selectorHtml += `<option value="custom" class="text-green-400">+ Load Custom .xsim-engine...</option>`;
    }
    engineSelector.innerHTML = selectorHtml;

    engineSelector.addEventListener('change', async (e) => {
        const val = e.target.value;
        if (val === 'd16a6_default') {
            loadEngine(_defaultD16A6Engine);
        } else if (val === 'shop') {
            if (purchasedEngines.length === 0 && !window.hasAdvancedMode) {
                alert(
                    "Heads up!\n\n" +
                    "While you're in the shop, you'll also want Advanced Mode to unlock:\n" +
                    "  • VVT / VTEC control\n" +
                    "  • Boost / turbo tuning\n" +
                    "  • Launch control\n" +
                    "  • Closed-loop fueling\n\n" +
                    "Look for the Advanced Mode upgrade alongside the engine packs!"
                );
            }
            window.open(window.shopEnginesUrl, '_blank');
            // Snap back to whichever engine is actually loaded
            engineSelector.value = lastLoadedEngine ? 'loaded_custom' : 'd16a6_default';
        } else if (val === 'custom') {
            document.getElementById('file-engine-upload').click();
        } else if (val === 'loaded_custom') {
            if (lastLoadedEngine) loadEngine(lastLoadedEngine);
        } else {
            fetchEngine(val);
        }
    });
}

const fileEngineUpload = document.getElementById('file-engine-upload');
if (fileEngineUpload) {
    fileEngineUpload.addEventListener('change', (e) => {
        const file = e.target.files[0];
        if (!file) { if (engineSelector) engineSelector.value = 'd16a6_default'; return; }

        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const loadedEngine = JSON.parse(event.target.result);
                if (loadedEngine.schema_type !== "engine_profile") {
                    alert("Invalid file: Must be an AlphaX engine_profile schema.");
                    if (engineSelector) engineSelector.value = 'd16a6_default';
                    return;
                }
                if (!loadedEngine.global_specs.firing_order) loadedEngine.global_specs.firing_order = "1234";
                if (!loadedEngine.global_specs.true_deadtime_ms) loadedEngine.global_specs.true_deadtime_ms = 0.70;
                if (loadedEngine.global_specs.trigger_base_offset === undefined) loadedEngine.global_specs.trigger_base_offset = 0;

                loadEngine(loadedEngine);

                if (engineSelector) {
                    const newOption = document.createElement('option');
                    newOption.value = 'loaded_custom';
                    newOption.text = loadedEngine.global_specs.display_name;
                    newOption.selected = true;
                    engineSelector.insertBefore(newOption, engineSelector.lastElementChild);
                }
            } catch (err) {
                alert("Failed to parse engine file: " + err.message);
                if (engineSelector) engineSelector.value = 'd16a6_default';
            }
        };
        reader.readAsText(file);
        e.target.value = '';
    });
}

// --- TRANSMISSION DATA & PROFILES ---

let currentTransmission = {
    schema_type: "transmission_profile",
    trans_id: "custom",
    name: "Custom Transmission",
    final_drive: 4.058,
    gears: [3.250, 1.900, 1.250, 0.909, 0.702, 0, 0, 0, 0, 0]
};

function loadTransmission(data) {
    if (data.schema_type !== "transmission_profile" || !Array.isArray(data.gears)) return;
    
    currentTransmission.name = data.name || "Loaded Transmission";
    currentTransmission.final_drive = data.final_drive || 4.058;
    for (let i = 0; i < 10; i++) {
        currentTransmission.gears[i] = data.gears[i] || 0;
    }
    
    renderTransmissionUI();
    
    // Update Custom Settings visibility based on selection
    const selector = document.getElementById('trans-selector');
    const customSettings = document.getElementById('trans-custom-settings');
    if (selector && customSettings) {
        customSettings.classList.toggle('hidden', selector.value !== 'custom');
    }
}

function renderTransmissionUI() {
    const fdEl = document.getElementById('trans-fd');
    if (fdEl) fdEl.value = currentTransmission.final_drive;
    for (let i = 1; i <= 10; i++) {
        const gearEl = document.getElementById(`trans-g${i}`);
        if (gearEl) gearEl.value = currentTransmission.gears[i-1] || 0;
    }
}

function updateTransmissionFromUI() {
    const fdEl = document.getElementById('trans-fd');
    if (fdEl) currentTransmission.final_drive = parseFloat(fdEl.value) || 0;
    for (let i = 1; i <= 10; i++) {
        const gearEl = document.getElementById(`trans-g${i}`);
        if (gearEl) currentTransmission.gears[i-1] = parseFloat(gearEl.value) || 0;
    }
}

function exportTransmission() {
    updateTransmissionFromUI();
    const fileName = (currentTransmission.name || "AlphaX_Transmission").replace(/\s+/g, '_') + ".xsim-trans";
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(currentTransmission, null, 2));
    const dlAnchorElem = document.createElement('a');
    dlAnchorElem.setAttribute("href", dataStr);
    dlAnchorElem.setAttribute("download", fileName);
    dlAnchorElem.click();
}

function initTransmissionSelector() {
    const transSelector = document.getElementById('trans-selector');
    if (!transSelector) return;

    // First option is always the current engine's default (text filled in by loadEngine)
    let html = '<option value="engine_default">Default: —</option>';

    // One option per owned/purchased engine
    hostedEngines.forEach((e, idx) => {
        html += `<option value="owned_${idx}">${e.name} Transmission</option>`;
    });

    // Custom ratios — advanced mode only
    if (window.hasAdvancedMode || (typeof advancedMode !== 'undefined' && advancedMode)) {
        html += `<option value="custom" class="text-orange-400">Custom Transmission</option>`;
    }

    // Local file import — only available when running as file://
    if (window.location.protocol === 'file:') {
        html += `<option value="import_local" class="text-green-400">+ Import .xsim-engine...</option>`;
    }

    transSelector.innerHTML = html;

    transSelector.addEventListener('change', (e) => {
        const val = e.target.value;
        const customSettings = document.getElementById('trans-custom-settings');

        if (val === 'engine_default') {
            if (customSettings) customSettings.classList.add('hidden');
            const dt = sampleEngine.default_transmission;
            if (dt) {
                currentTransmission.name = dt.trans_id || "Engine Default";
                currentTransmission.final_drive = dt.final_drive || 4.0;
                for (let i = 0; i < 10; i++) currentTransmission.gears[i] = dt.gears?.[i] ?? 0;
                renderTransmissionUI();
                if (typeof updateCategoryTicker === 'function') updateCategoryTicker();
            }
        } else if (val === 'import_local') {
            const fileInput = document.getElementById('file-trans-upload');
            if (fileInput) fileInput.click();
            // Revert selector immediately — it'll be updated after file loads
            transSelector.value = 'engine_default';
        } else if (val === 'custom') {
            if (customSettings) customSettings.classList.remove('hidden');
            currentTransmission.name = "Custom Transmission";
            updateTransmissionFromUI();
        } else if (val.startsWith('owned_')) {
            if (customSettings) customSettings.classList.add('hidden');
            const idx = parseInt(val.slice(6));
            const entry = hostedEngines[idx];
            if (!entry) return;
            transSelector.disabled = true;
            fetch(entry.url)
                .then(r => r.json())
                .then(engineData => {
                    const dt = engineData.default_transmission;
                    if (dt) {
                        currentTransmission.name = dt.trans_id || dt.name || entry.name;
                        currentTransmission.final_drive = dt.final_drive || 4.0;
                        for (let i = 0; i < 10; i++) currentTransmission.gears[i] = dt.gears?.[i] ?? 0;
                        renderTransmissionUI();
                        if (typeof updateCategoryTicker === 'function') updateCategoryTicker();
                    }
                })
                .catch(err => console.warn('Could not load transmission from engine file:', err))
                .finally(() => { transSelector.disabled = false; });
        }
    });

    document.querySelectorAll('.trans-input').forEach(input => {
        input.addEventListener('change', updateTransmissionFromUI);
    });
}

const fileTransUpload = document.getElementById('file-trans-upload');
if (fileTransUpload) {
    fileTransUpload.addEventListener('change', (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const data = JSON.parse(event.target.result);
                let dt = null;
                if (data.schema_type === 'transmission_profile') {
                    dt = data;
                } else if (data.schema_type === 'engine_profile' && data.default_transmission) {
                    dt = data.default_transmission;
                } else {
                    alert("Invalid file: must be an .xsim-engine or .xsim-trans file with transmission data.");
                    return;
                }
                currentTransmission.name = dt.trans_id || dt.name || file.name;
                currentTransmission.final_drive = dt.final_drive || 4.0;
                for (let i = 0; i < 10; i++) currentTransmission.gears[i] = dt.gears?.[i] ?? 0;
                renderTransmissionUI();
                if (typeof updateCategoryTicker === 'function') updateCategoryTicker();

                const transSelector = document.getElementById('trans-selector');
                if (transSelector) {
                    // Replace or reuse the "loaded_trans" option
                    let existing = transSelector.querySelector('option[value="loaded_trans"]');
                    if (!existing) {
                        existing = document.createElement('option');
                        existing.value = 'loaded_trans';
                        transSelector.insertBefore(existing, transSelector.lastElementChild);
                    }
                    existing.text = currentTransmission.name;
                    transSelector.value = 'loaded_trans';
                }
            } catch (err) {
                alert("Failed to parse file: " + err.message);
            }
        };
        reader.readAsText(file);
        fileTransUpload.value = '';
    });
}

window.addEventListener('DOMContentLoaded', () => {
    initTransmissionSelector();
});
